import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Button } from '../ui/button'
import { useNavigate } from '@tanstack/react-router'
import NAV_ITEMS, { BOTTOM_NAV_ITEMS } from '@/lib/constants'
import { useAuth } from '@/lib/hooks/useAuth'
import { useCrisisStore } from '@/lib/stores/crisisStore'

interface MobileNavProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileNav({ isOpen, onClose }: MobileNavProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { activeCrisis } = useCrisisStore()

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'

  // Helper to check if user has required permissions for a nav item
  const hasNavItemPermission = useMemo(() => {
    return (item: (typeof NAV_ITEMS)[0]): boolean => {
      // If no permissions required, allow access
      if (!item.requiredPermissions || item.requiredPermissions.length === 0) return true

      // No user = no permissions
      if (!user) return false

      // Super admin bypass
      if (user.isSuperAdmin) return true

      // Check if user has any of the required permissions (OR logic)
      const userPermissions = user.permissions || []
      return item.requiredPermissions.some(perm => userPermissions.includes(perm))
    }
  }, [user])

  // Filter nav items based on permissions and crisis state
  const visibleNavItems = useMemo(() => {
    return NAV_ITEMS.filter(item => {
      if (!hasNavItemPermission(item)) return false
      if (item.requiresCrisis && !activeCrisis) return false
      if (item.requiresCrisis === false && activeCrisis) return false
      return true
    })
  }, [hasNavItemPermission, activeCrisis])

  const visibleBottomNavItems = useMemo(() => {
    return BOTTOM_NAV_ITEMS.filter(item => {
      // Show login only when user is NOT authenticated
      if (item.id === 'login' && user) {
        return false
      }

      // Only show Profile and Logout when user is authenticated
      if ((item.id === 'profile' || item.id === 'logout') && !user) {
        return false
      }

      // Permission check
      return hasNavItemPermission(item)
    })
  }, [hasNavItemPermission, user])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleBottomNavClick = async (item: (typeof BOTTOM_NAV_ITEMS)[0]) => {
    if (item.id === 'logout') {
      await logout()
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    } else {
      navigate({ to: item.to })
      onClose()
    }
  }

  const handleNavClick = (to: string) => {
    navigate({ to })
    onClose()
  }

  return (
    <>
      <div
        className={`mobile-nav-backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
        role="presentation"
      />

      <aside
        className={`mobile-nav ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigatiemenu"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="brand flex items-center justify-start">
              <img
                src="/crisisbuddy_logo.png"
                alt="BGCH Crisis Buddy logo"
                className="object-contain  w-[40%]"
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/10"
              aria-label="Menu sluiten"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto p-4" aria-label="Hoofdnavigatie">
            <ul className="space-y-2" role="list">
              {visibleNavItems.map(item => {
                const isActive = pathname === item.to
                return (
                  <li key={item.id}>
                    <Button
                      variant="white"
                      className={`nav-item w-full justify-start min-h-[48px] ${isActive ? 'active' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => handleNavClick(item.to)}
                    >
                      <span aria-hidden="true">{item.icon({ className: 'w-5 h-5' })}</span>
                      <span className="ml-3 text-base">{t(item.titleKey)}</span>
                    </Button>
                  </li>
                )
              })}
            </ul>
          </nav>

          {/* Bottom Navigation */}
          {visibleBottomNavItems.length > 0 && (
            <div className="border-t border-white/10 p-4">
              <nav aria-label="Gebruikersnavigatie">
                <ul className="space-y-2" role="list">
                  {visibleBottomNavItems.map(item => {
                    const isActive = pathname === item.to
                    return (
                      <li key={item.id}>
                        <Button
                          variant="white"
                          className={`nav-item w-full justify-start min-h-[48px] ${isActive ? 'active' : ''}`}
                          aria-current={isActive ? 'page' : undefined}
                          onClick={() => handleBottomNavClick(item)}
                        >
                          <span aria-hidden="true">{item.icon({ className: 'w-5 h-5' })}</span>
                          <span className="ml-3 text-base">{t(item.titleKey)}</span>
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              </nav>
            </div>
          )}
        </div>
      </aside>
    </>
  )
}
